import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Search, X, GripVertical, Plus, Clock, Zap, Globe, ChevronDown, ChevronUp, Calendar, Copy, Settings, Keyboard, Sun, Moon } from 'lucide-react';
// Google Calendar feature hidden for now (requires Google verification)
// import { GoogleAuthProvider } from './context/GoogleAuthContext';
// import MeetingModal from './components/MeetingModal';

const TimezoneCompareApp = () => {
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const getUserCity = (timezone) => {
    const parts = timezone.split('/');
    return parts[parts.length - 1].replace(/_/g, ' ');
  };

  const [timezones, setTimezones] = useState(() => {
    try {
      const saved = localStorage.getItem('mitime-timezones');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Failed to load saved timezones');
    }
    return [
      { id: 1, zone: userTimezone, label: getUserCity(userTimezone), workStart: 9, workEnd: 18, pinned: true },
      { id: 2, zone: 'America/New_York', label: 'New York', workStart: 9, workEnd: 18, pinned: false }
    ];
  });

  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedHour, setSelectedHour] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('day');
  const [granularity, setGranularity] = useState(60);
  const [quickCompare, setQuickCompare] = useState('');
  const [editingWorkHours, setEditingWorkHours] = useState(null);
  const [hideWeekends, setHideWeekends] = useState(false);
  const [comparisonMode, setComparisonMode] = useState(false);
  const [comparisonTime, setComparisonTime] = useState('');
  const [legendCollapsed, setLegendCollapsed] = useState(true);
  const [toast, setToast] = useState(null);
  const [calendarExported, setCalendarExported] = useState(false);
  const [copied, setCopied] = useState(false);
  const [jumpError, setJumpError] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [showKeyboardHelp, setShowKeyboardHelp] = useState(false);
  const scrollRef = useRef(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const baseDate = new Date(selectedDate);
  const [showSettings, setShowSettings] = useState(false);
  const [language, setLanguage] = useState('en');

  const [use24Hour, setUse24Hour] = useState(() => {
    try {
      const saved = localStorage.getItem('mitime-24hour');
      return saved ? JSON.parse(saved) : true;
    } catch (e) {
      return true;
    }
  });

  const [darkMode, setDarkMode] = useState(() => {
    try {
      const saved = localStorage.getItem('mitime-darkmode');
      if (saved !== null) {
        return JSON.parse(saved);
      }
      // Default to system preference
      if (typeof window !== 'undefined') {
        return window.matchMedia('(prefers-color-scheme: dark)').matches;
      }
      return true;
    } catch (e) {
      return true;
    }
  });

  // Sync dark mode with document
  useEffect(() => {
    if (darkMode) {
      document.documentElement.removeAttribute('data-theme');
    } else {
      document.documentElement.setAttribute('data-theme', 'light');
    }
    localStorage.setItem('mitime-darkmode', JSON.stringify(darkMode));
  }, [darkMode]);

  // Listen for system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      const saved = localStorage.getItem('mitime-darkmode');
      // Only auto-switch if user hasn't manually set preference
      if (saved === null) {
        setDarkMode(e.matches);
      }
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const hasSeenOnboarding = typeof window !== 'undefined' ? localStorage.getItem('mitime-onboarding-seen') : null;
    if (!hasSeenOnboarding && timezones.length === 2) {
      setShowOnboarding(true);
    }
  }, []);

  const completeOnboarding = () => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('mitime-onboarding-seen', 'true');
    }
    setShowOnboarding(false);
    setOnboardingStep(0);
  };

  const onboardingSteps = [
    {
      title: "Welcome to MITime",
      description: "An efficient approach to coordinating meetings across time zones.",
      target: null
    },
    {
      title: "Add Timezones",
      description: "Click the amber button below to add timezones. Search by city, country, or UTC offset.",
      target: "add-timezone"
    },
    {
      title: "Smart Suggestions",
      description: "MITime automatically finds the best meeting times when you have 2+ timezones.",
      target: "smart-suggestions"
    },
    {
      title: "Visual Timeline",
      description: "Green indicates work hours, darker tones mean sleep hours. Hover any slot for details.",
      target: "timeline"
    },
    {
      title: "Keyboard Navigation",
      description: "Use arrow keys to navigate, T for today, / to search. Press ? to see all shortcuts.",
      target: null
    }
  ];

  const countryFlags = {
    'America/New_York': '🇺🇸', 'America/Chicago': '🇺🇸', 'America/Denver': '🇺🇸',
    'America/Los_Angeles': '🇺🇸', 'America/Phoenix': '🇺🇸', 'America/Anchorage': '🇺🇸',
    'America/Honolulu': '🇺🇸', 'America/Toronto': '🇨🇦', 'America/Vancouver': '🇨🇦',
    'America/Montreal': '🇨🇦', 'America/Mexico_City': '🇲🇽', 'America/Tijuana': '🇲🇽',
    'America/Havana': '🇨🇺', 'America/Sao_Paulo': '🇧🇷', 'America/Buenos_Aires': '🇦🇷',
    'America/Santiago': '🇨🇱', 'America/Lima': '🇵🇪', 'America/Bogota': '🇨🇴',
    'America/Caracas': '🇻🇪', 'America/Montevideo': '🇺🇾', 'Europe/London': '🇬🇧',
    'Europe/Dublin': '🇮🇪', 'Europe/Lisbon': '🇵🇹', 'Atlantic/Reykjavik': '🇮🇸',
    'Europe/Paris': '🇫🇷', 'Europe/Berlin': '🇩🇪', 'Europe/Amsterdam': '🇳🇱',
    'Europe/Brussels': '🇧🇪', 'Europe/Madrid': '🇪🇸', 'Europe/Barcelona': '🇪🇸',
    'Europe/Rome': '🇮🇹', 'Europe/Milan': '🇮🇹', 'Europe/Zurich': '🇨🇭',
    'Europe/Vienna': '🇦🇹', 'Europe/Prague': '🇨🇿', 'Europe/Budapest': '🇭🇺',
    'Europe/Warsaw': '🇵🇱', 'Europe/Stockholm': '🇸🇪', 'Europe/Oslo': '🇳🇴',
    'Europe/Copenhagen': '🇩🇰', 'Europe/Athens': '🇬🇷', 'Europe/Bucharest': '🇷🇴',
    'Europe/Sofia': '🇧🇬', 'Europe/Helsinki': '🇫🇮', 'Europe/Tallinn': '🇪🇪',
    'Europe/Riga': '🇱🇻', 'Europe/Vilnius': '🇱🇹', 'Europe/Kiev': '🇺🇦',
    'Europe/Istanbul': '🇹🇷', 'Europe/Moscow': '🇷🇺', 'Europe/Minsk': '🇧🇾',
    'Asia/Nicosia': '🇨🇾', 'Europe/Belgrade': '🇷🇸', 'Europe/Zagreb': '🇭🇷',
    'Asia/Dubai': '🇦🇪', 'Asia/Abu_Dhabi': '🇦🇪', 'Asia/Riyadh': '🇸🇦',
    'Asia/Kuwait': '🇰🇼', 'Asia/Doha': '🇶🇦', 'Asia/Bahrain': '🇧🇭',
    'Asia/Muscat': '🇴🇲', 'Asia/Jerusalem': '🇮🇱', 'Asia/Beirut': '🇱🇧',
    'Asia/Amman': '🇯🇴', 'Asia/Damascus': '🇸🇾', 'Asia/Baghdad': '🇮🇶',
    'Asia/Tehran': '🇮🇷', 'Africa/Cairo': '🇪🇬', 'Africa/Johannesburg': '🇿🇦',
    'Africa/Cape_Town': '🇿🇦', 'Africa/Lagos': '🇳🇬', 'Africa/Nairobi': '🇰🇪',
    'Africa/Casablanca': '🇲🇦', 'Africa/Algiers': '🇩🇿', 'Africa/Tunis': '🇹🇳',
    'Africa/Accra': '🇬🇭', 'Africa/Dar_es_Salaam': '🇹🇿', 'Asia/Kolkata': '🇮🇳',
    'Asia/Karachi': '🇵🇰', 'Asia/Dhaka': '🇧🇩', 'Asia/Colombo': '🇱🇰',
    'Asia/Kathmandu': '🇳🇵', 'Asia/Bangkok': '🇹🇭', 'Asia/Singapore': '🇸🇬',
    'Asia/Kuala_Lumpur': '🇲🇾', 'Asia/Jakarta': '🇮🇩', 'Asia/Manila': '🇵🇭',
    'Asia/Ho_Chi_Minh': '🇻🇳', 'Asia/Yangon': '🇲🇲', 'Asia/Hong_Kong': '🇭🇰',
    'Asia/Shanghai': '🇨🇳', 'Asia/Beijing': '🇨🇳', 'Asia/Taipei': '🇹🇼',
    'Asia/Seoul': '🇰🇷', 'Asia/Tokyo': '🇯🇵', 'Asia/Vladivostok': '🇷🇺',
    'Asia/Ulaanbaatar': '🇲🇳', 'Asia/Tashkent': '🇺🇿', 'Asia/Almaty': '🇰🇿',
    'Asia/Yekaterinburg': '🇷🇺', 'Australia/Perth': '🇦🇺', 'Australia/Adelaide': '🇦🇺',
    'Australia/Sydney': '🇦🇺', 'Australia/Melbourne': '🇦🇺', 'Australia/Brisbane': '🇦🇺',
    'Pacific/Auckland': '🇳🇿', 'Pacific/Fiji': '🇫🇯', 'Pacific/Guam': '🇬🇺',
    'Pacific/Port_Moresby': '🇵🇬'
  };

  const translations = {
    en: {
      title: 'MITime',
      subtitle: 'Time Zone Coordination',
      addTimezone: 'Add Timezone',
      compareTime: 'Compare',
      copyTimes: 'Copy',
      copied: 'Copied',
      addToCalendar: 'Calendar',
      downloaded: 'Saved',
      searchPlaceholder: 'Search city, country, or UTC offset...',
      visualGuide: 'Visual Guide',
      workHours: 'Work hours',
      sleepHours: 'Sleep hours',
      currentTime: 'Current time'
    },
    ja: {
      title: 'MITime',
      subtitle: '天体時刻調整',
      addTimezone: 'タイムゾーン追加',
      compareTime: '比較',
      copyTimes: 'コピー',
      copied: 'コピー完了',
      addToCalendar: 'カレンダー',
      downloaded: '保存完了',
      searchPlaceholder: '都市、国、UTCで検索...',
      visualGuide: 'ガイド',
      workHours: '勤務時間',
      sleepHours: '睡眠時間',
      currentTime: '現在時刻'
    }
  };

  const t = translations[language];

  const showToast = (message, duration = 3000) => {
    setToast(message);
    const timer = setTimeout(() => setToast(null), duration);
    return () => clearTimeout(timer);
  };

  const getRelativeTime = (date, timezone) => {
    const now = new Date();
    const targetDate = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
    const diffMs = targetDate - now;
    const diffHours = Math.round(diffMs / (1000 * 60 * 60));

    if (diffHours === 0) return 'now';
    if (diffHours === 1) return 'in 1h';
    if (diffHours === -1) return '1h ago';
    if (diffHours > 0) return `in ${diffHours}h`;
    return `${Math.abs(diffHours)}h ago`;
  };

  const formatTime = (hour, minute, use24Hour) => {
    if (use24Hour) {
      return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
    }
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
  };

  const getUTCOffset = (timezone, date = new Date()) => {
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'longOffset'
      });
      const parts = formatter.formatToParts(date);
      const offsetPart = parts.find(part => part.type === 'timeZoneName');
      return offsetPart ? offsetPart.value : '';
    } catch (e) {
      return '';
    }
  };

  const allTimezones = [
    { zone: 'America/New_York', label: 'New York', region: 'North America' },
    { zone: 'America/Chicago', label: 'Chicago', region: 'North America' },
    { zone: 'America/Denver', label: 'Denver', region: 'North America' },
    { zone: 'America/Phoenix', label: 'Phoenix', region: 'North America' },
    { zone: 'America/Los_Angeles', label: 'Los Angeles', region: 'North America' },
    { zone: 'America/Anchorage', label: 'Anchorage', region: 'North America' },
    { zone: 'America/Honolulu', label: 'Honolulu', region: 'North America' },
    { zone: 'America/Toronto', label: 'Toronto', region: 'North America' },
    { zone: 'America/Vancouver', label: 'Vancouver', region: 'North America' },
    { zone: 'America/Montreal', label: 'Montreal', region: 'North America' },
    { zone: 'America/Halifax', label: 'Halifax', region: 'North America' },
    { zone: 'America/Winnipeg', label: 'Winnipeg', region: 'North America' },
    { zone: 'America/Mexico_City', label: 'Mexico City', region: 'North America' },
    { zone: 'America/Tijuana', label: 'Tijuana', region: 'North America' },
    { zone: 'America/Cancun', label: 'Cancún', region: 'North America' },
    { zone: 'America/Guatemala', label: 'Guatemala City', region: 'Central America' },
    { zone: 'America/Panama', label: 'Panama City', region: 'Central America' },
    { zone: 'America/Costa_Rica', label: 'San José', region: 'Central America' },
    { zone: 'America/Havana', label: 'Havana', region: 'Caribbean' },
    { zone: 'America/Jamaica', label: 'Kingston', region: 'Caribbean' },
    { zone: 'America/Santo_Domingo', label: 'Santo Domingo', region: 'Caribbean' },
    { zone: 'America/Puerto_Rico', label: 'San Juan', region: 'Caribbean' },
    { zone: 'America/Sao_Paulo', label: 'São Paulo', region: 'South America' },
    { zone: 'America/Buenos_Aires', label: 'Buenos Aires', region: 'South America' },
    { zone: 'America/Santiago', label: 'Santiago', region: 'South America' },
    { zone: 'America/Lima', label: 'Lima', region: 'South America' },
    { zone: 'America/Bogota', label: 'Bogotá', region: 'South America' },
    { zone: 'America/Caracas', label: 'Caracas', region: 'South America' },
    { zone: 'America/Montevideo', label: 'Montevideo', region: 'South America' },
    { zone: 'Europe/London', label: 'London', region: 'Europe' },
    { zone: 'Europe/Dublin', label: 'Dublin', region: 'Europe' },
    { zone: 'Europe/Lisbon', label: 'Lisbon', region: 'Europe' },
    { zone: 'Atlantic/Reykjavik', label: 'Reykjavik', region: 'Europe' },
    { zone: 'Europe/Paris', label: 'Paris', region: 'Europe' },
    { zone: 'Europe/Berlin', label: 'Berlin', region: 'Europe' },
    { zone: 'Europe/Amsterdam', label: 'Amsterdam', region: 'Europe' },
    { zone: 'Europe/Brussels', label: 'Brussels', region: 'Europe' },
    { zone: 'Europe/Madrid', label: 'Madrid', region: 'Europe' },
    { zone: 'Europe/Barcelona', label: 'Barcelona', region: 'Europe' },
    { zone: 'Europe/Rome', label: 'Rome', region: 'Europe' },
    { zone: 'Europe/Milan', label: 'Milan', region: 'Europe' },
    { zone: 'Europe/Zurich', label: 'Zurich', region: 'Europe' },
    { zone: 'Europe/Vienna', label: 'Vienna', region: 'Europe' },
    { zone: 'Europe/Prague', label: 'Prague', region: 'Europe' },
    { zone: 'Europe/Budapest', label: 'Budapest', region: 'Europe' },
    { zone: 'Europe/Warsaw', label: 'Warsaw', region: 'Europe' },
    { zone: 'Europe/Stockholm', label: 'Stockholm', region: 'Europe' },
    { zone: 'Europe/Oslo', label: 'Oslo', region: 'Europe' },
    { zone: 'Europe/Copenhagen', label: 'Copenhagen', region: 'Europe' },
    { zone: 'Europe/Athens', label: 'Athens', region: 'Europe' },
    { zone: 'Europe/Bucharest', label: 'Bucharest', region: 'Europe' },
    { zone: 'Europe/Sofia', label: 'Sofia', region: 'Europe' },
    { zone: 'Europe/Helsinki', label: 'Helsinki', region: 'Europe' },
    { zone: 'Europe/Tallinn', label: 'Tallinn', region: 'Europe' },
    { zone: 'Europe/Riga', label: 'Riga', region: 'Europe' },
    { zone: 'Europe/Vilnius', label: 'Vilnius', region: 'Europe' },
    { zone: 'Europe/Kiev', label: 'Kyiv', region: 'Europe' },
    { zone: 'Europe/Istanbul', label: 'Istanbul', region: 'Europe' },
    { zone: 'Europe/Moscow', label: 'Moscow', region: 'Europe' },
    { zone: 'Europe/Minsk', label: 'Minsk', region: 'Europe' },
    { zone: 'Asia/Nicosia', label: 'Nicosia', region: 'Europe' },
    { zone: 'Europe/Belgrade', label: 'Belgrade', region: 'Europe' },
    { zone: 'Europe/Zagreb', label: 'Zagreb', region: 'Europe' },
    { zone: 'Asia/Dubai', label: 'Dubai', region: 'Middle East' },
    { zone: 'Asia/Riyadh', label: 'Riyadh', region: 'Middle East' },
    { zone: 'Asia/Kuwait', label: 'Kuwait City', region: 'Middle East' },
    { zone: 'Asia/Doha', label: 'Doha', region: 'Middle East' },
    { zone: 'Asia/Bahrain', label: 'Manama', region: 'Middle East' },
    { zone: 'Asia/Jerusalem', label: 'Jerusalem', region: 'Middle East' },
    { zone: 'Asia/Beirut', label: 'Beirut', region: 'Middle East' },
    { zone: 'Asia/Baghdad', label: 'Baghdad', region: 'Middle East' },
    { zone: 'Asia/Tehran', label: 'Tehran', region: 'Middle East' },
    { zone: 'Africa/Cairo', label: 'Cairo', region: 'Africa' },
    { zone: 'Africa/Johannesburg', label: 'Johannesburg', region: 'Africa' },
    { zone: 'Africa/Lagos', label: 'Lagos', region: 'Africa' },
    { zone: 'Africa/Nairobi', label: 'Nairobi', region: 'Africa' },
    { zone: 'Africa/Casablanca', label: 'Casablanca', region: 'Africa' },
    { zone: 'Africa/Algiers', label: 'Algiers', region: 'Africa' },
    { zone: 'Africa/Tunis', label: 'Tunis', region: 'Africa' },
    { zone: 'Africa/Accra', label: 'Accra', region: 'Africa' },
    { zone: 'Asia/Kolkata', label: 'Mumbai', region: 'Asia' },
    { zone: 'Asia/Kolkata', label: 'Delhi', region: 'Asia' },
    { zone: 'Asia/Kolkata', label: 'Bangalore', region: 'Asia' },
    { zone: 'Asia/Karachi', label: 'Karachi', region: 'Asia' },
    { zone: 'Asia/Dhaka', label: 'Dhaka', region: 'Asia' },
    { zone: 'Asia/Colombo', label: 'Colombo', region: 'Asia' },
    { zone: 'Asia/Kathmandu', label: 'Kathmandu', region: 'Asia' },
    { zone: 'Asia/Bangkok', label: 'Bangkok', region: 'Asia' },
    { zone: 'Asia/Singapore', label: 'Singapore', region: 'Asia' },
    { zone: 'Asia/Kuala_Lumpur', label: 'Kuala Lumpur', region: 'Asia' },
    { zone: 'Asia/Jakarta', label: 'Jakarta', region: 'Asia' },
    { zone: 'Asia/Manila', label: 'Manila', region: 'Asia' },
    { zone: 'Asia/Ho_Chi_Minh', label: 'Ho Chi Minh City', region: 'Asia' },
    { zone: 'Asia/Yangon', label: 'Yangon', region: 'Asia' },
    { zone: 'Asia/Hong_Kong', label: 'Hong Kong', region: 'Asia' },
    { zone: 'Asia/Shanghai', label: 'Shanghai', region: 'Asia' },
    { zone: 'Asia/Beijing', label: 'Beijing', region: 'Asia' },
    { zone: 'Asia/Taipei', label: 'Taipei', region: 'Asia' },
    { zone: 'Asia/Seoul', label: 'Seoul', region: 'Asia' },
    { zone: 'Asia/Tokyo', label: 'Tokyo', region: 'Asia' },
    { zone: 'Asia/Ulaanbaatar', label: 'Ulaanbaatar', region: 'Asia' },
    { zone: 'Asia/Tashkent', label: 'Tashkent', region: 'Central Asia' },
    { zone: 'Asia/Almaty', label: 'Almaty', region: 'Central Asia' },
    { zone: 'Asia/Yekaterinburg', label: 'Yekaterinburg', region: 'Russia' },
    { zone: 'Asia/Vladivostok', label: 'Vladivostok', region: 'Russia' },
    { zone: 'Australia/Perth', label: 'Perth', region: 'Oceania' },
    { zone: 'Australia/Adelaide', label: 'Adelaide', region: 'Oceania' },
    { zone: 'Australia/Sydney', label: 'Sydney', region: 'Oceania' },
    { zone: 'Australia/Melbourne', label: 'Melbourne', region: 'Oceania' },
    { zone: 'Australia/Brisbane', label: 'Brisbane', region: 'Oceania' },
    { zone: 'Pacific/Auckland', label: 'Auckland', region: 'Oceania' },
    { zone: 'Pacific/Fiji', label: 'Suva', region: 'Oceania' },
    { zone: 'Pacific/Guam', label: 'Guam', region: 'Oceania' },
    { zone: 'Pacific/Port_Moresby', label: 'Port Moresby', region: 'Oceania' }
  ];

  const filteredTimezones = allTimezones.filter(tz => {
    const search = searchTerm.toLowerCase().trim();
    if (!search) return true;

    const countryMap = {
      'usa': ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Phoenix', 'America/Anchorage', 'America/Honolulu'],
      'us': ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Phoenix', 'America/Anchorage', 'America/Honolulu'],
      'united states': ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Phoenix', 'America/Anchorage', 'America/Honolulu'],
      'uk': ['Europe/London'],
      'britain': ['Europe/London'],
      'england': ['Europe/London'],
      'japan': ['Asia/Tokyo'],
      'china': ['Asia/Shanghai', 'Asia/Beijing'],
      'india': ['Asia/Kolkata'],
      'australia': ['Australia/Sydney', 'Australia/Melbourne', 'Australia/Brisbane', 'Australia/Perth', 'Australia/Adelaide'],
      'canada': ['America/Toronto', 'America/Vancouver', 'America/Montreal'],
      'germany': ['Europe/Berlin'],
      'france': ['Europe/Paris'],
      'spain': ['Europe/Madrid', 'Europe/Barcelona'],
      'italy': ['Europe/Rome', 'Europe/Milan'],
      'brazil': ['America/Sao_Paulo'],
      'mexico': ['America/Mexico_City', 'America/Tijuana']
    };

    for (const [country, zones] of Object.entries(countryMap)) {
      if (search === country || search.startsWith(country + ' ')) {
        if (zones.includes(tz.zone)) return true;
      }
    }

    const utcMatch = search.match(/(?:utc)?([+-]?\d+(?:\.\d+)?)/);
    if (utcMatch) {
      const searchOffset = parseFloat(utcMatch[1]);
      const now = new Date();
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: tz.zone,
        timeZoneName: 'shortOffset'
      });
      const parts = formatter.formatToParts(now);
      const offsetStr = parts.find(p => p.type === 'timeZoneName')?.value;
      if (offsetStr) {
        const offsetMatch = offsetStr.match(/GMT([+-])(\d+)(?::(\d+))?/);
        if (offsetMatch) {
          const sign = offsetMatch[1] === '+' ? 1 : -1;
          const hours = parseInt(offsetMatch[2]);
          const mins = offsetMatch[3] ? parseInt(offsetMatch[3]) : 0;
          const offset = sign * (hours + mins / 60);
          if (Math.abs(offset - searchOffset) < 0.5) return true;
        }
      }
    }

    return tz.label.toLowerCase().includes(search) ||
      tz.zone.toLowerCase().includes(search) ||
      tz.region.toLowerCase().includes(search);
  });

  // Save timezones to localStorage
  useEffect(() => {
    localStorage.setItem('mitime-timezones', JSON.stringify(timezones));
  }, [timezones]);

  // Save 24hour preference
  useEffect(() => {
    localStorage.setItem('mitime-24hour', JSON.stringify(use24Hour));
  }, [use24Hour]);

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Keyboard shortcuts
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      switch (e.key) {
        case 'ArrowLeft':
          setSelectedDate(prev => {
            const newDate = new Date(prev);
            newDate.setDate(newDate.getDate() - 1);
            return newDate;
          });
          break;
        case 'ArrowRight':
          setSelectedDate(prev => {
            const newDate = new Date(prev);
            newDate.setDate(newDate.getDate() + 1);
            return newDate;
          });
          break;
        case 't':
        case 'T':
          setSelectedDate(new Date());
          break;
        case '/':
          e.preventDefault();
          setShowSearch(true);
          break;
        case '?':
          setShowKeyboardHelp(true);
          break;
        case 'd':
        case 'D':
          setViewMode('day');
          break;
        case 'w':
        case 'W':
          setViewMode('week');
          break;
        case 'c':
        case 'C':
          setComparisonMode(!comparisonMode);
          break;
        case 'Escape':
          setShowSearch(false);
          setShowSettings(false);
          setShowKeyboardHelp(false);
          setShowOnboarding(false);
          break;
        case 'Enter':
          if (selectedHour !== null) {
            handleCalendarExport();
          }
          break;
        default:
          if (e.key >= '1' && e.key <= '9') {
            const idx = parseInt(e.key) - 1;
            if (idx < timezones.length) {
              // Could add timezone selection feature here
            }
          }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedHour, comparisonMode, timezones.length]);

  const addTimezone = (tz) => {
    const newTz = {
      id: Date.now(),
      zone: tz.zone,
      label: tz.label,
      workStart: 9,
      workEnd: 18,
      pinned: false
    };
    setTimezones([...timezones, newTz]);
    setShowSearch(false);
    setSearchTerm('');
    showToast(`Added ${tz.label}`);
  };

  const removeTimezone = (id) => {
    setTimezones(timezones.filter(tz => tz.id !== id));
    showToast('Timezone removed');
  };

  const updateWorkHours = (id, field, value) => {
    const numValue = Math.max(0, Math.min(23, parseInt(value) || 0));
    setTimezones(timezones.map(tz =>
      tz.id === id ? { ...tz, [field]: numValue } : tz
    ));
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      const newTimezones = [...timezones];
      const [removed] = newTimezones.splice(draggedIndex, 1);
      newTimezones.splice(index, 0, removed);
      setTimezones(newTimezones);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const getTimeSlots = (date, timezone, workStart, workEnd) => {
    const slots = [];
    const baseDate = new Date(date);
    baseDate.setHours(0, 0, 0, 0);
    const slotsPerDay = 24 * (60 / granularity);

    for (let i = 0; i < slotsPerDay; i++) {
      const slotDate = new Date(baseDate);
      const totalMinutes = i * granularity;
      slotDate.setMinutes(totalMinutes);

      const tzDate = new Date(slotDate.toLocaleString('en-US', { timeZone: timezone }));
      const hour = tzDate.getHours();
      const minute = tzDate.getMinutes();

      const isWorkHours = hour >= workStart && hour < workEnd;
      const isSleepHours = hour >= 23 || hour < 7;

      slots.push({
        hour,
        minute,
        day: tzDate.getDay(),
        dayName: tzDate.toLocaleDateString('en-US', { weekday: 'short' }),
        fullDate: tzDate,
        isWorkHours,
        isSleepHours
      });
    }

    return slots;
  };

  const getWeekDays = (startDate) => {
    const days = [];
    const start = new Date(startDate);
    start.setDate(start.getDate() - start.getDay());

    for (let i = 0; i < 7; i++) {
      if (hideWeekends && (i === 0 || i === 6)) continue;
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      days.push(day);
    }

    return days;
  };

  const weekDays = getWeekDays(selectedDate);
  const currentHour = currentTime.getHours();

  const getSmartSuggestions = useMemo(() => {
    if (timezones.length < 2) return [];

    const suggestions = [];
    const today = new Date();

    for (let d = 0; d < 7; d++) {
      const day = new Date(today);
      day.setDate(today.getDate() + d);

      if (day.getDay() === 0 || day.getDay() === 6) continue;

      for (let startHour = 0; startHour < 24; startHour++) {
        let consecutiveGoodHours = 0;
        let bestEndHour = startHour;

        for (let hour = startHour; hour < 24 && consecutiveGoodHours < 4; hour++) {
          let allInWorkHours = true;
          let anyInSleepHours = false;

          for (const tz of timezones) {
            const testDate = new Date(day);
            testDate.setHours(hour, 0, 0, 0);
            const tzDate = new Date(testDate.toLocaleString('en-US', { timeZone: tz.zone }));
            const tzHour = tzDate.getHours();

            if (tzHour < tz.workStart || tzHour >= tz.workEnd) {
              allInWorkHours = false;
            }
            if (tzHour >= 23 || tzHour < 7) {
              anyInSleepHours = true;
            }
          }

          if (allInWorkHours && !anyInSleepHours) {
            consecutiveGoodHours++;
            bestEndHour = hour;
          } else if (consecutiveGoodHours > 0) {
            break;
          }
        }

        if (consecutiveGoodHours >= 1) {
          const startTime = formatTime(startHour, 0, use24Hour);
          const endTime = formatTime(bestEndHour + 1, 0, use24Hour);

          suggestions.push({
            day,
            dayLabel: d === 0 ? 'Today' : d === 1 ? 'Tomorrow' : day.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
            startTime,
            endTime,
            timeOfDay: startHour < 12 ? 'Morning' : startHour < 17 ? 'Afternoon' : 'Evening',
            duration: consecutiveGoodHours,
            confidence: consecutiveGoodHours >= 3 ? 3 : consecutiveGoodHours >= 2 ? 2 : 1,
            overlapPercent: Math.round((consecutiveGoodHours / 8) * 100),
            slots: Array.from({ length: consecutiveGoodHours }, (_, i) => startHour + i)
          });

          startHour = bestEndHour;
        }
      }
    }

    return suggestions
      .sort((a, b) => b.duration - a.duration || b.confidence - a.confidence)
      .slice(0, 3);
  }, [timezones, use24Hour]);

  const comparisonResults = useMemo(() => {
    if (!comparisonTime || timezones.length === 0) return [];

    const timeMatch = comparisonTime.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/i);
    if (!timeMatch) return [];

    let hour = parseInt(timeMatch[1]);
    const minute = parseInt(timeMatch[2]) || 0;
    const period = timeMatch[3]?.toLowerCase();

    if (period === 'pm' && hour < 12) hour += 12;
    if (period === 'am' && hour === 12) hour = 0;

    const results = [];
    const baseDate = new Date(selectedDate);
    baseDate.setHours(hour, minute, 0, 0);

    for (const tz of timezones) {
      const tzTime = new Date(baseDate.toLocaleString('en-US', { timeZone: tz.zone }));
      results.push({
        timezone: tz,
        time: formatTime(tzTime.getHours(), tzTime.getMinutes(), use24Hour)
      });
    }

    return results;
  }, [comparisonTime, timezones, selectedDate, use24Hour]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const timeWarnings = useMemo(() => {
    if (selectedHour === null) return [];

    const warnings = [];

    for (const tz of timezones) {
      const slots = getTimeSlots(baseDate, tz.zone, tz.workStart, tz.workEnd);
      const slot = slots[selectedHour];

      if (slot?.isSleepHours) {
        warnings.push({
          type: 'sleep',
          message: `${tz.label} is in sleep hours (${formatTime(slot.hour, slot.minute, use24Hour)})`
        });
      } else if (!slot?.isWorkHours) {
        warnings.push({
          type: 'outside',
          message: `${tz.label} is outside work hours (${formatTime(slot.hour, slot.minute, use24Hour)})`
        });
      }
    }

    const slots = getTimeSlots(baseDate, timezones[0].zone, timezones[0].workStart, timezones[0].workEnd);
    const slot = slots[selectedHour];
    if (slot && (slot.day === 0 || slot.day === 6)) {
      warnings.push({
        type: 'weekend',
        message: 'This is on a weekend'
      });
    }

    return warnings;
  }, [selectedHour, timezones, baseDate, granularity, use24Hour]);

  const getDSTWarning = (timezone) => {
    const now = new Date();
    const twoWeeksLater = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);

    const nowOffset = new Date(now.toLocaleString('en-US', { timeZone: timezone })).getTimezoneOffset();
    const laterOffset = new Date(twoWeeksLater.toLocaleString('en-US', { timeZone: timezone })).getTimezoneOffset();

    if (nowOffset !== laterOffset) {
      return 'DST change upcoming';
    }
    return null;
  };

  const handleExport = () => {
    const lines = timezones.map(tz => {
      const time = new Date().toLocaleString('en-US', {
        timeZone: tz.zone,
        hour: 'numeric',
        minute: '2-digit',
        hour12: !use24Hour
      });
      return `${tz.label}: ${time}`;
    });

    navigator.clipboard.writeText(lines.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    showToast('Times copied to clipboard');
  };

  const handleCalendarExport = () => {
    if (selectedHour === null) return;

    const slot = getTimeSlots(baseDate, timezones[0].zone, timezones[0].workStart, timezones[0].workEnd)[selectedHour];
    const startDate = slot.fullDate;
    const endDate = new Date(startDate.getTime() + granularity * 60 * 1000);

    const formatICSDate = (date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const ics = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//MITime//Meeting//EN',
      'BEGIN:VEVENT',
      `DTSTART:${formatICSDate(startDate)}`,
      `DTEND:${formatICSDate(endDate)}`,
      'SUMMARY:Meeting (via MITime)',
      `DESCRIPTION:Scheduled across ${timezones.length} timezones`,
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');

    const blob = new Blob([ics], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'meeting.ics';
    link.click();
    URL.revokeObjectURL(url);

    setCalendarExported(true);
    setTimeout(() => setCalendarExported(false), 2000);
    showToast('Calendar event downloaded');
  };

  const handleQuickCompare = () => {
    const timeMatch = quickCompare.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/i);
    if (!timeMatch) {
      setJumpError(true);
      setTimeout(() => setJumpError(false), 500);
      return;
    }

    let hour = parseInt(timeMatch[1]);
    const minute = parseInt(timeMatch[2]) || 0;
    const period = timeMatch[3]?.toLowerCase();

    if (period === 'pm' && hour < 12) hour += 12;
    if (period === 'am' && hour === 12) hour = 0;

    const slotIndex = Math.floor((hour * 60 + minute) / granularity);
    setSelectedHour(slotIndex);

    if (scrollRef.current) {
      const slotWidth = granularity === 60 ? 80 : granularity === 30 ? 60 : 50;
      scrollRef.current.scrollLeft = slotIndex * slotWidth - 160;
    }

    setQuickCompare('');
  };

  const weekViewData = useMemo(() => {
    return timezones.map(tz => {
      return weekDays.map(day => {
        const hourlyOverlap = {};

        for (let hour = 0; hour < 24; hour++) {
          let allInWorkHours = true;
          let anyInSleepHours = false;
          const tzDetails = [];

          for (const innerTz of timezones) {
            const testDate = new Date(day);
            testDate.setHours(hour, 0, 0, 0);
            const tzDate = new Date(testDate.toLocaleString('en-US', { timeZone: innerTz.zone }));
            const tzHour = tzDate.getHours();

            const isWork = tzHour >= innerTz.workStart && tzHour < innerTz.workEnd;
            const isSleep = tzHour >= 23 || tzHour < 7;

            if (!isWork) allInWorkHours = false;
            if (isSleep) anyInSleepHours = true;

            tzDetails.push({
              label: innerTz.label,
              time: formatTime(tzHour, 0, use24Hour),
              isWork,
              isSleep
            });
          }

          hourlyOverlap[hour] = { allInWorkHours, anyInSleepHours, tzDetails };
        }

        return { day, hourlyOverlap };
      });
    });
  }, [timezones, weekDays, use24Hour]);

  const weeklyOverview = weekDays.map((day, dayIdx) => {
    let overlapHours = 0;
    const tzData = weekViewData[0]?.[dayIdx];

    if (tzData) {
      for (let hour = 0; hour < 24; hour++) {
        if (tzData.hourlyOverlap[hour]?.allInWorkHours && !tzData.hourlyOverlap[hour]?.anyInSleepHours) {
          overlapHours++;
        }
      }
    }

    return {
      day,
      overlapHours,
      isToday: day.toDateString() === new Date().toDateString(),
      dayIdx
    };
  });

  const getAvailabilityScore = (hour, dayIdx, data) => {
    if (!data[0]?.[dayIdx]) return 0;

    const overlap = data[0][dayIdx].hourlyOverlap[hour];
    if (!overlap) return 0;

    if (overlap.anyInSleepHours) return 10;
    if (overlap.allInWorkHours) return 100;

    let workCount = 0;
    for (const detail of overlap.tzDetails) {
      if (detail.isWork) workCount++;
    }

    return Math.round((workCount / overlap.tzDetails.length) * 100);
  };

  const getHeatmapColor = (score) => {
    if (score >= 90) return 'bg-emerald-900/40 border-emerald-700/30';
    if (score >= 70) return 'bg-green-900/30 border-green-700/30';
    if (score >= 50) return 'bg-yellow-900/25 border-yellow-700/30';
    if (score >= 30) return 'bg-orange-900/25 border-orange-700/30';
    return 'bg-red-900/20 border-red-700/30';
  };

  const getSlotClasses = (slot, isCurrentSlot, isSelected) => {
    let base = 'time-slot ';

    if (slot.isWorkHours && !slot.isSleepHours) {
      base += 'time-slot-work ';
    } else if (slot.isSleepHours) {
      base += 'time-slot-sleep ';
    } else {
      base += 'time-slot-off ';
    }

    if (isCurrentSlot) {
      base += 'time-slot-current ';
    }

    if (isSelected) {
      base += 'time-slot-selected ';
    }

    return base;
  };

  return (
    <div className="observatory-bg grid-overlay min-h-screen p-4 sm:p-6 lg:p-8 relative z-10">
      {/* Toast Notification */}
      {toast && (
        <div className="fixed top-6 right-6 z-50 animate-fade-in">
          <div className="card-glass px-4 py-3 flex items-center gap-3">
            <span className="text-amber">✓</span>
            <span className="text-starlight text-sm">{toast}</span>
          </div>
        </div>
      )}

      {/* Onboarding Modal */}
      {showOnboarding && (
        <div className="fixed inset-0 modal-overlay z-50 flex items-center justify-center p-4 animate-fade-in">
          <div className="modal-content max-w-lg w-full p-8 relative">
            <div className="corner-ornament corner-ornament-tl" />
            <div className="corner-ornament corner-ornament-tr" />
            <div className="corner-ornament corner-ornament-bl" />
            <div className="corner-ornament corner-ornament-br" />

            <div className="text-center mb-8">
              <div className="text-5xl mb-4">{onboardingStep === 0 ? '🌌' : onboardingStep === 4 ? '⌨️' : '✨'}</div>
              <h2 className="text-display-lg text-starlight mb-2">
                {onboardingSteps[onboardingStep].title}
              </h2>
              <p className="text-moonlight text-sm">
                {onboardingSteps[onboardingStep].description}
              </p>
            </div>

            <div className="flex justify-center gap-2 mb-6">
              {onboardingSteps.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-2 h-2 rounded-full transition-all ${idx === onboardingStep ? 'bg-amber w-6' : 'bg-nebula'}`}
                />
              ))}
            </div>

            <div className="flex gap-3">
              {onboardingStep > 0 && (
                <button
                  onClick={() => setOnboardingStep(onboardingStep - 1)}
                  className="btn-observatory flex-1"
                >
                  Back
                </button>
              )}
              {onboardingStep < onboardingSteps.length - 1 ? (
                <button
                  onClick={() => setOnboardingStep(onboardingStep + 1)}
                  className="btn-observatory btn-primary flex-1"
                >
                  Next
                </button>
              ) : (
                <button
                  onClick={completeOnboarding}
                  className="btn-observatory btn-primary flex-1"
                >
                  Get Started
                </button>
              )}
            </div>

            <button
              onClick={completeOnboarding}
              className="absolute top-4 right-4 text-moonlight hover:text-starlight transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Keyboard Shortcuts Modal */}
      {showKeyboardHelp && (
        <div className="fixed inset-0 modal-overlay z-50 flex items-center justify-center p-4 animate-fade-in">
          <div className="modal-content max-w-2xl w-full p-8 relative max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-display-lg text-starlight flex items-center gap-3">
                <Keyboard className="text-amber" size={24} />
                Keyboard Shortcuts
              </h2>
              <button
                onClick={() => setShowKeyboardHelp(false)}
                className="text-moonlight hover:text-starlight transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-8">
              <div>
                <h3 className="text-label text-amber mb-4">Navigation</h3>
                <div className="space-y-3">
                  {[
                    ['←', 'Previous day'],
                    ['→', 'Next day'],
                    ['T', 'Go to today'],
                    ['/', 'Search timezones']
                  ].map(([key, desc]) => (
                    <div key={key} className="flex items-center justify-between">
                      <span className="text-moonlight text-sm">{desc}</span>
                      <kbd className="px-2 py-1 bg-nebula border border-amber/20 rounded text-xs text-starlight font-mono">
                        {key}
                      </kbd>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-label text-amber mb-4">Views & Actions</h3>
                <div className="space-y-3">
                  {[
                    ['D', 'Day view'],
                    ['W', 'Week view'],
                    ['C', 'Compare mode'],
                    ['Enter', 'Export to calendar'],
                    ['Esc', 'Close modal']
                  ].map(([key, desc]) => (
                    <div key={key} className="flex items-center justify-between">
                      <span className="text-moonlight text-sm">{desc}</span>
                      <kbd className="px-2 py-1 bg-nebula border border-amber/20 rounded text-xs text-starlight font-mono">
                        {key}
                      </kbd>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-amber/10 border border-amber/20 rounded">
              <p className="text-sm text-amber">
                Press <kbd className="px-1.5 py-0.5 bg-nebula rounded text-xs">?</kbd> anytime to see this help
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 modal-overlay z-50 flex items-center justify-center p-4 animate-fade-in">
          <div className="modal-content max-w-xl w-full max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-cosmos p-6 border-b border-amber/20 flex items-center justify-between">
              <h2 className="text-display-lg text-starlight flex items-center gap-3">
                <Settings className="text-amber" size={24} />
                Settings
              </h2>
              <button
                onClick={() => setShowSettings(false)}
                className="text-moonlight hover:text-starlight transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-8">
              {/* Display Settings */}
              <div>
                <h3 className="text-label text-amber mb-4">Display</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-starlight text-sm">Dark Mode</div>
                      <div className="text-moonlight text-xs">Toggle between light and dark themes</div>
                    </div>
                    <button
                      onClick={() => setDarkMode(!darkMode)}
                      className={`toggle-switch ${darkMode ? 'active' : ''}`}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-starlight text-sm">24-Hour Format</div>
                      <div className="text-moonlight text-xs">Show times in 24-hour format</div>
                    </div>
                    <button
                      onClick={() => setUse24Hour(!use24Hour)}
                      className={`toggle-switch ${use24Hour ? 'active' : ''}`}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-starlight text-sm">Hide Weekends</div>
                      <div className="text-moonlight text-xs">Show only business days in week view</div>
                    </div>
                    <button
                      onClick={() => setHideWeekends(!hideWeekends)}
                      className={`toggle-switch ${hideWeekends ? 'active' : ''}`}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-starlight text-sm">Collapse Legend</div>
                      <div className="text-moonlight text-xs">Hide visual guide by default</div>
                    </div>
                    <button
                      onClick={() => setLegendCollapsed(!legendCollapsed)}
                      className={`toggle-switch ${legendCollapsed ? 'active' : ''}`}
                    />
                  </div>
                </div>
              </div>

              {/* Time Granularity */}
              <div>
                <h3 className="text-label text-amber mb-4">Time Granularity</h3>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    [60, '1 Hour'],
                    [30, '30 Min'],
                    [15, '15 Min']
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      onClick={() => setGranularity(value)}
                      className={`btn-observatory ${granularity === value ? 'btn-primary' : ''}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Language */}
              <div>
                <h3 className="text-label text-amber mb-4">Language</h3>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setLanguage('en')}
                    className={`btn-observatory ${language === 'en' ? 'btn-primary' : ''}`}
                  >
                    🇺🇸 English
                  </button>
                  <button
                    onClick={() => setLanguage('ja')}
                    className={`btn-observatory ${language === 'ja' ? 'btn-primary' : ''}`}
                  >
                    🇯🇵 日本語
                  </button>
                </div>
              </div>

              {/* Data Management */}
              <div>
                <h3 className="text-label text-amber mb-4">Data</h3>
                <div className="space-y-3">
                  <button
                    onClick={() => {
                      if (window.confirm('This will reset all your timezones to default. Continue?')) {
                        setTimezones([
                          { id: 1, zone: userTimezone, label: getUserCity(userTimezone), workStart: 9, workEnd: 18, pinned: true },
                          { id: 2, zone: 'America/New_York', label: 'New York', workStart: 9, workEnd: 18, pinned: false }
                        ]);
                        showToast('Timezones reset to default');
                      }
                    }}
                    className="w-full btn-observatory text-orange-400 border-orange-400/30 hover:bg-orange-500/10"
                  >
                    Reset Timezones
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm('This will clear all saved data. Continue?')) {
                        localStorage.removeItem('mitime-timezones');
                        localStorage.removeItem('mitime-favorites');
                        localStorage.removeItem('mitime-24hour');
                        localStorage.removeItem('mitime-recent-zones');
                        localStorage.removeItem('mitime-onboarding-seen');
                        showToast('All data cleared. Refresh the page.');
                      }
                    }}
                    className="w-full btn-observatory text-red-400 border-red-400/30 hover:bg-red-500/10"
                  >
                    Clear All Data
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Container */}
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <header className="card-observatory p-6 mb-6 animate-fade-in">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-amber to-copper flex items-center justify-center">
                <Globe className="text-midnight" size={24} />
              </div>
              <div>
                <h1 className="text-display-xl text-starlight">{t.title}</h1>
                <p className="text-label text-moonlight tracking-widest">{t.subtitle}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setComparisonMode(!comparisonMode)}
                className={`btn-observatory flex items-center gap-2 ${comparisonMode ? 'btn-primary' : ''}`}
              >
                <Clock size={14} />
                {t.compareTime}
              </button>

              <button
                onClick={handleExport}
                className={`btn-observatory flex items-center gap-2 ${copied ? 'btn-primary' : ''}`}
              >
                <Copy size={14} />
                {copied ? t.copied : t.copyTimes}
              </button>

              {selectedHour !== null && (
                <button
                  onClick={handleCalendarExport}
                  className={`btn-observatory flex items-center gap-2 ${calendarExported ? 'btn-primary' : ''}`}
                >
                  <Calendar size={14} />
                  {calendarExported ? t.downloaded : t.addToCalendar}
                </button>
              )}

              <button
                onClick={() => setShowSettings(true)}
                className="btn-observatory p-2.5"
              >
                <Settings size={16} />
              </button>

              <button
                onClick={() => setShowKeyboardHelp(true)}
                className="btn-observatory p-2.5"
              >
                <Keyboard size={16} />
              </button>

              {/* Theme Toggle */}
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="theme-toggle"
                title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                <Sun size={16} className="sun-icon text-amber" />
                <Moon size={16} className="moon-icon text-amber" />
              </button>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex rounded overflow-hidden border border-amber/20">
              <button
                onClick={() => setViewMode('day')}
                className={`px-4 py-2 text-sm font-mono transition-all ${viewMode === 'day'
                  ? 'bg-amber text-midnight'
                  : 'bg-nebula text-moonlight hover:bg-cosmos'
                  }`}
              >
                Day
              </button>
              <button
                onClick={() => setViewMode('week')}
                className={`px-4 py-2 text-sm font-mono border-l border-amber/20 transition-all ${viewMode === 'week'
                  ? 'bg-amber text-midnight'
                  : 'bg-nebula text-moonlight hover:bg-cosmos'
                  }`}
              >
                Week
              </button>
            </div>

            <input
              type="date"
              value={selectedDate.toISOString().split('T')[0]}
              onChange={(e) => setSelectedDate(new Date(e.target.value))}
              className="input-observatory"
            />

            <button
              onClick={() => setSelectedDate(new Date())}
              className={`btn-observatory ${selectedDate.toDateString() === new Date().toDateString()
                ? 'btn-primary'
                : selectedDate.toDateString() !== new Date().toDateString() ? 'animate-pulse-gold' : ''
                }`}
            >
              Today
            </button>
          </div>

          {/* Quick Jump - Day View Only */}
          {viewMode === 'day' && (
            <div className="mt-4 flex items-center gap-3 p-3 bg-deep-space rounded border border-amber/10">
              <Clock size={16} className="text-amber" />
              <input
                type="text"
                placeholder="Jump to time: 2pm, 14:30, 9am"
                value={quickCompare}
                onChange={(e) => setQuickCompare(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleQuickCompare()}
                className={`flex-1 bg-transparent border-none outline-none text-starlight text-sm placeholder-moonlight/40 ${jumpError ? 'shake' : ''}`}
              />
              <button onClick={handleQuickCompare} className="btn-observatory text-xs py-1.5">
                Jump
              </button>
            </div>
          )}

          {/* Keyboard tip */}
          {viewMode === 'day' && (
            <div className="mt-3 text-center">
              <span className="text-moonlight/60 text-xs">
                Use <kbd className="px-1.5 py-0.5 bg-nebula rounded text-xs border border-amber/20">←</kbd> <kbd className="px-1.5 py-0.5 bg-nebula rounded text-xs border border-amber/20">→</kbd> to navigate,{' '}
                <kbd className="px-1.5 py-0.5 bg-nebula rounded text-xs border border-amber/20">T</kbd> for today,{' '}
                <kbd className="px-1.5 py-0.5 bg-nebula rounded text-xs border border-amber/20">?</kbd> for shortcuts
              </span>
            </div>
          )}
        </header>

        {/* Time Warnings */}
        {selectedHour !== null && timeWarnings.length > 0 && (
          <div className="card-observatory p-5 mb-6 animate-fade-in">
            <div className="flex items-start gap-4">
              <span className="text-2xl">⚠️</span>
              <div className="flex-1">
                <h3 className="text-display-md text-starlight mb-3">Scheduling Considerations</h3>
                <div className="space-y-2">
                  {timeWarnings.map((warning, idx) => (
                    <div
                      key={idx}
                      className={`text-sm px-3 py-2 rounded font-medium ${warning.type === 'sleep'
                        ? 'warning-sleep'
                        : warning.type === 'weekend'
                          ? 'warning-weekend'
                          : 'warning-outside'
                        }`}
                    >
                      {warning.message}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Prompt to add timezone */}
        {timezones.length === 1 && (
          <div className="card-observatory p-10 mb-6 text-center border-2 border-dashed border-amber/30 animate-fade-in">
            <div className="text-6xl mb-4">🌍</div>
            <h3 className="text-display-lg text-starlight mb-2">Add Another Timezone</h3>
            <p className="text-moonlight text-sm mb-6 max-w-md mx-auto">
              MITime helps you find the perfect meeting times across different time zones. Add another timezone to see smart suggestions.
            </p>
            <button
              onClick={() => setShowSearch(true)}
              className="btn-observatory btn-primary animate-pulse-gold"
            >
              <Plus size={16} className="inline mr-2" />
              Add Timezone
            </button>
          </div>
        )}

        {/* Smart Suggestions */}
        {timezones.length > 1 && getSmartSuggestions.length > 0 && (
          <div id="smart-suggestions" className="card-observatory p-6 mb-6 animate-fade-in">
            <div className="flex items-center gap-3 mb-5">
              <Zap className="text-amber" size={20} />
              <h3 className="text-display-md text-starlight">Best Meeting Times This Week</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {getSmartSuggestions.map((suggestion, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setSelectedDate(suggestion.day);
                    setViewMode('day');
                    setSelectedHour(suggestion.slots[0]);
                    if (scrollRef.current) {
                      const slotWidth = granularity === 60 ? 80 : granularity === 30 ? 60 : 50;
                      setTimeout(() => {
                        scrollRef.current.scrollLeft = suggestion.slots[0] * slotWidth - 160;
                      }, 100);
                    }
                  }}
                  className="text-left p-5 rounded-lg bg-deep-space border border-amber/20 hover:border-amber/50 transition-all hover:transform hover:scale-[1.02]"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-label text-amber">{suggestion.dayLabel}</span>
                    <div className="flex gap-0.5">
                      {[...Array(suggestion.confidence)].map((_, i) => (
                        <span key={i} className="text-gold text-sm">★</span>
                      ))}
                    </div>
                  </div>
                  <div className="text-starlight text-lg font-display font-semibold mb-2">
                    {suggestion.startTime} – {suggestion.endTime}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-moonlight text-xs">
                      {suggestion.timeOfDay} · {suggestion.duration}h
                    </span>
                    <span className="text-xs px-2 py-1 bg-emerald-900/40 text-emerald-400 rounded border border-emerald-700/30">
                      {suggestion.overlapPercent}% overlap
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Comparison Mode */}
        {comparisonMode && (
          <div className="card-observatory p-6 mb-6 animate-fade-in">
            <div className="flex items-center gap-3 mb-5">
              <Clock className="text-amber" size={20} />
              <h3 className="text-display-md text-starlight">Time Comparison</h3>
            </div>
            <div className="p-4 bg-deep-space rounded border border-amber/10 mb-4">
              <label className="text-label text-moonlight mb-2 block">
                Enter a time in {timezones[0]?.label}:
              </label>
              <input
                type="text"
                placeholder="e.g., 2pm, 14:30, 9am"
                value={comparisonTime}
                onChange={(e) => setComparisonTime(e.target.value)}
                className="input-observatory w-full"
              />
            </div>

            {comparisonResults.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {comparisonResults.map((result, idx) => (
                  <div
                    key={idx}
                    className="p-4 bg-deep-space rounded border border-amber/10"
                  >
                    <div className="text-label text-moonlight mb-1">{result.timezone.label}</div>
                    <div className="text-starlight text-xl font-display font-semibold">{result.time}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Timezone Rows - Day View */}
        {timezones.length === 0 ? (
          <div className="card-observatory p-12 mb-6 text-center border-2 border-dashed border-amber/30 animate-fade-in">
            <div className="text-8xl mb-6">⏰</div>
            <h2 className="text-display-xl text-starlight mb-3">Welcome to MITime</h2>
            <p className="text-moonlight mb-8 max-w-lg mx-auto">
              The effective approach to coordinating meetings across time zones. Get started by adding your first timezone.
            </p>
            <button
              onClick={() => setShowSearch(true)}
              className="btn-observatory btn-primary text-lg px-8 py-3"
            >
              <Plus size={20} className="inline mr-2" />
              Add Your First Timezone
            </button>
          </div>
        ) : viewMode === 'day' ? (
          <div className="space-y-4" id="timeline">
            {timezones.map((tz, tzIndex) => {
              const slots = getTimeSlots(baseDate, tz.zone, tz.workStart, tz.workEnd);
              const firstDate = slots[0].fullDate;
              const isToday = firstDate.getDate() === baseDate.getDate();
              const dateLabel = firstDate.toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                timeZone: tz.zone
              });
              const dstWarning = getDSTWarning(tz.zone);
              const currentTimeStr = new Date().toLocaleString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: !use24Hour,
                timeZone: tz.zone
              });
              const utcOffset = getUTCOffset(tz.zone);

              return (
                <div
                  key={tz.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, tzIndex)}
                  onDragOver={(e) => handleDragOver(e, tzIndex)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, tzIndex)}
                  onDragEnd={handleDragEnd}
                  className={`card-observatory overflow-hidden transition-all animate-fade-in-up stagger-${Math.min(tzIndex + 1, 5)} ${dragOverIndex === tzIndex ? 'ring-2 ring-amber' : ''
                    } ${draggedIndex === tzIndex ? 'opacity-50' : ''}`}
                  style={{ animationDelay: `${tzIndex * 0.05}s` }}
                >
                  {/* Timezone Header */}
                  <div className="flex items-center justify-between p-5 border-b border-amber/10 bg-deep-space/50">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        {tzIndex < 9 && (
                          <div className="w-6 h-6 rounded bg-nebula border border-amber/20 flex items-center justify-center">
                            <span className="text-amber text-xs font-mono">{tzIndex + 1}</span>
                          </div>
                        )}
                        <div className="p-1.5 rounded cursor-grab active:cursor-grabbing hover:bg-nebula transition-colors">
                          <GripVertical size={16} className="text-moonlight" />
                        </div>
                        <div className="w-10 h-10 rounded bg-nebula flex items-center justify-center">
                          <span className="text-xl">{countryFlags[tz.zone] || '🌍'}</span>
                        </div>
                        <div>
                          <div className="text-starlight font-display text-lg font-semibold">{tz.label}</div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-time text-amber">{currentTimeStr}</span>
                            <span className="text-xs px-2 py-0.5 bg-nebula rounded text-moonlight">
                              {getRelativeTime(new Date(), tz.zone)}
                            </span>
                            <span className="text-xs text-moonlight/60">{utcOffset}</span>
                            {!isToday && (
                              <span className="text-xs px-2 py-0.5 bg-amber/20 text-amber rounded">
                                {dateLabel}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      {dstWarning && (
                        <div className="mt-2 text-xs px-2 py-1 bg-orange-900/30 text-orange-400 rounded inline-flex items-center gap-1 border border-orange-700/30">
                          <span>⚠</span> {dstWarning}
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      {editingWorkHours === tz.id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="0"
                            max="23"
                            value={tz.workStart}
                            onChange={(e) => updateWorkHours(tz.id, 'workStart', e.target.value)}
                            className="input-observatory w-14 text-center"
                          />
                          <span className="text-moonlight">–</span>
                          <input
                            type="number"
                            min="0"
                            max="23"
                            value={tz.workEnd}
                            onChange={(e) => updateWorkHours(tz.id, 'workEnd', e.target.value)}
                            className="input-observatory w-14 text-center"
                          />
                          <button
                            onClick={() => setEditingWorkHours(null)}
                            className="btn-observatory btn-primary py-1.5 px-3"
                          >
                            ✓
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setEditingWorkHours(tz.id)}
                          className="btn-observatory text-xs"
                        >
                          Work: {formatTime(tz.workStart, 0, use24Hour).split(':')[0]}–{formatTime(tz.workEnd, 0, use24Hour).split(':')[0]}
                        </button>
                      )}

                      {timezones.length > 1 && (
                        <button
                          onClick={() => removeTimezone(tz.id)}
                          className="btn-observatory p-2 text-red-400 border-red-400/30 hover:bg-red-500/10"
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Time Slots */}
                  <div
                    ref={tz.id === timezones[0].id ? scrollRef : null}
                    className="overflow-x-auto scrollbar-hide"
                    style={{ scrollBehavior: 'smooth' }}
                  >
                    <div className="inline-flex">
                      {slots.map((slot, idx) => {
                        const isCurrentSlot = slot.hour === currentHour &&
                          (granularity === 30 ? slot.minute === (currentTime.getMinutes() < 30 ? 0 : 30) :
                            granularity === 15 ? Math.floor(currentTime.getMinutes() / 15) * 15 === slot.minute : true) &&
                          isToday;
                        const slotWidth = granularity === 60 ? 80 : granularity === 30 ? 60 : 50;
                        const isSelected = selectedHour === idx;

                        return (
                          <div
                            key={idx}
                            onClick={() => setSelectedHour(idx)}
                            className={`
                              flex-shrink-0 h-20 flex flex-col items-center justify-center
                              cursor-pointer transition-all relative group
                              ${getSlotClasses(slot, isCurrentSlot, isSelected)}
                            `}
                            style={{ width: `${slotWidth}px` }}
                          >
                            {slot.isSleepHours && (
                              <div className="absolute top-1.5 right-1.5 text-sm opacity-40">💤</div>
                            )}
                            <div className="text-time text-starlight">
                              {formatTime(slot.hour, slot.minute, use24Hour)}
                            </div>

                            {/* Hover tooltip */}
                            <div className="tooltip bottom-full mb-2 hidden group-hover:block">
                              {formatTime(slot.hour, slot.minute, use24Hour)}
                              <span className="ml-2 text-moonlight">
                                {slot.isWorkHours && !slot.isSleepHours ? '💼' : slot.isSleepHours ? '💤' : '🏠'}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          // Week View
          <div className="space-y-4">
            {/* Week Overview */}
            <div className="card-observatory p-6">
              <h3 className="text-display-md text-starlight mb-5">Weekly Meeting Availability</h3>
              <div className={`grid gap-3 ${hideWeekends ? 'grid-cols-5' : 'grid-cols-7'}`}>
                {weeklyOverview.map(({ day, overlapHours, isToday, dayIdx }) => (
                  <button
                    key={dayIdx}
                    onClick={() => {
                      setSelectedDate(day);
                      setViewMode('day');
                    }}
                    className={`p-4 rounded-lg text-center transition-all relative ${isToday
                      ? 'bg-amber/20 border-2 border-amber animate-pulse-gold'
                      : 'bg-deep-space border border-amber/10 hover:border-amber/30'
                      }`}
                  >
                    {isToday && (
                      <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 px-2 py-0.5 text-xs bg-amber text-midnight rounded font-mono">
                        TODAY
                      </div>
                    )}
                    <div className="text-label text-moonlight mb-1">
                      {day.toLocaleDateString('en-US', { weekday: 'short' })}
                    </div>
                    <div className="text-starlight text-xl font-display font-semibold mb-2">
                      {day.getDate()}
                    </div>
                    {overlapHours > 0 ? (
                      <div>
                        <div className={`text-2xl font-display font-bold ${overlapHours >= 4
                          ? 'text-emerald-400'
                          : overlapHours >= 2
                            ? 'text-amber'
                            : 'text-orange-400'
                          }`}>
                          {overlapHours}h
                        </div>
                        <div className="text-moonlight text-xs">overlap</div>
                      </div>
                    ) : (
                      <div className="text-moonlight/60 text-sm">No overlap</div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Detailed Heatmap */}
            <div className="card-observatory overflow-hidden">
              <div className="p-5 border-b border-amber/10">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-display-md text-starlight">Availability Heatmap</h3>
                    <p className="text-moonlight text-xs mt-1">Warmer colors = better overlap across all timezones</p>
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="text-moonlight">Legend:</span>
                    <div className="flex items-center gap-1">
                      <div className="w-4 h-4 rounded bg-emerald-900/40 border border-emerald-700/30"></div>
                      <span className="text-moonlight">Perfect</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-4 h-4 rounded bg-yellow-900/25 border border-yellow-700/30"></div>
                      <span className="text-moonlight">OK</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-4 h-4 rounded bg-red-900/20 border border-red-700/30"></div>
                      <span className="text-moonlight">Poor</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-amber/10 bg-deep-space/50">
                      <th className="p-3 text-left text-label text-amber sticky left-0 bg-deep-space/50 z-10">
                        Hour
                      </th>
                      {weekDays.map((day, idx) => (
                        <th key={idx} className="p-3 text-center">
                          <div className="text-label text-starlight">{day.toLocaleDateString('en-US', { weekday: 'short' })}</div>
                          <div className="text-xs text-moonlight">
                            {day.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: 24 }, (_, hour) => (
                      <tr key={hour} className="border-b border-amber/5">
                        <td className="p-2 text-time text-moonlight sticky left-0 bg-cosmos z-10">
                          {formatTime(hour, 0, use24Hour)}
                        </td>
                        {weekDays.map((day, dayIdx) => {
                          const tzData = weekViewData[0]?.[dayIdx];
                          if (!tzData) return <td key={dayIdx}></td>;

                          const overlap = tzData.hourlyOverlap[hour];
                          const score = getAvailabilityScore(hour, dayIdx, weekViewData);
                          const currentHourMatch = hour === currentTime.getHours() &&
                            day.toDateString() === new Date().toDateString();

                          return (
                            <td
                              key={dayIdx}
                              className={`p-0 transition-all cursor-pointer group relative ${currentHourMatch
                                ? 'bg-amber/30 ring-2 ring-amber ring-inset'
                                : getHeatmapColor(score)
                                }`}
                            >
                              <div className="p-3 text-center">
                                <div className={`text-lg font-mono font-semibold ${score >= 90 ? 'text-emerald-400' :
                                  score >= 70 ? 'text-green-400' :
                                    score >= 50 ? 'text-yellow-400' :
                                      score >= 30 ? 'text-orange-400' :
                                        'text-red-400'
                                  }`}>
                                  {score}%
                                </div>
                                <div className="text-xs mt-1">
                                  {overlap.allInWorkHours && !overlap.anyInSleepHours && '✓'}
                                  {overlap.anyInSleepHours && '💤'}
                                </div>
                              </div>

                              {/* Detailed Tooltip */}
                              <div className="tooltip bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block min-w-[200px] text-left">
                                <div className="font-semibold border-b border-amber/20 pb-1 mb-2">
                                  {day.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                                  <span className="ml-2 text-moonlight text-xs">at {formatTime(hour, 0, use24Hour)}</span>
                                </div>
                                <div className="mb-2 text-sm">
                                  Availability: <span className={
                                    score >= 90 ? 'text-emerald-400' :
                                      score >= 70 ? 'text-green-400' :
                                        score >= 50 ? 'text-yellow-400' :
                                          score >= 30 ? 'text-orange-400' :
                                            'text-red-400'
                                  }>{score}%</span>
                                </div>
                                {overlap.tzDetails.map((detail, i) => (
                                  <div key={i} className="flex items-center gap-2 py-0.5 text-xs">
                                    <span className="text-starlight">{detail.label}:</span>
                                    <span className="text-moonlight">{detail.time}</span>
                                    {detail.isSleep && <span>💤</span>}
                                    {detail.isWork && !detail.isSleep && <span className="text-emerald-400">✓</span>}
                                  </div>
                                ))}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Add Timezone */}
        <div className="mt-6 relative" id="add-timezone">
          {!showSearch ? (
            <div className="flex gap-3">
              <button
                onClick={() => setShowSearch(true)}
                className="btn-observatory btn-primary flex items-center gap-2"
              >
                <Plus size={16} />
                {t.addTimezone}
              </button>

              {timezones.length > 2 && (
                <button
                  onClick={() => {
                    if (window.confirm('Clear all timezones except your local one?')) {
                      setTimezones([timezones[0]]);
                    }
                  }}
                  className="btn-observatory text-red-400 border-red-400/30 hover:bg-red-500/10"
                >
                  Clear All
                </button>
              )}
            </div>
          ) : (
            <div className="card-observatory p-6 animate-fade-in">
              <div className="flex items-center gap-3 mb-4">
                <Search className="text-amber" size={18} />
                <input
                  type="text"
                  placeholder={t.searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-1 bg-transparent border-none outline-none text-starlight placeholder-moonlight/40"
                  autoFocus
                />
                <button
                  onClick={() => {
                    setShowSearch(false);
                    setSearchTerm('');
                  }}
                  className="text-moonlight hover:text-starlight transition-colors"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="max-h-96 overflow-y-auto space-y-1 pr-2">
                {Object.entries(
                  filteredTimezones.reduce((acc, tz) => {
                    if (!acc[tz.region]) acc[tz.region] = [];
                    acc[tz.region].push(tz);
                    return acc;
                  }, {})
                ).map(([region, tzs]) => (
                  <div key={region} className="mb-4">
                    <div className="text-label text-amber px-3 py-2">{region}</div>
                    <div className="space-y-1">
                      {tzs.map((tz) => {
                        const alreadyAdded = timezones.find(t => t.zone === tz.zone);
                        return (
                          <button
                            key={`${tz.zone}-${tz.label}`}
                            onClick={() => !alreadyAdded && addTimezone(tz)}
                            disabled={alreadyAdded}
                            className={`w-full text-left px-4 py-3 rounded transition-all ${alreadyAdded
                              ? 'bg-nebula/30 text-moonlight/50 cursor-not-allowed'
                              : 'hover:bg-nebula text-starlight'
                              }`}
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-sm">{tz.label}</span>
                              {alreadyAdded && (
                                <span className="text-xs px-2 py-0.5 bg-emerald-900/40 text-emerald-400 rounded border border-emerald-700/30">
                                  Added
                                </span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="mt-6 card-observatory overflow-hidden">
          <button
            onClick={() => setLegendCollapsed(!legendCollapsed)}
            className="w-full flex items-center justify-between p-4 hover:bg-deep-space/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Globe className="text-amber" size={18} />
              <h3 className="text-starlight font-display">{t.visualGuide}</h3>
            </div>
            {legendCollapsed ? <ChevronDown size={18} className="text-moonlight" /> : <ChevronUp size={18} className="text-moonlight" />}
          </button>

          {!legendCollapsed && (
            <div className="p-6 pt-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 border-t border-amber/10">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 time-slot-work rounded"></div>
                <span className="text-sm text-moonlight">{t.workHours}</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 time-slot-sleep rounded flex items-center justify-center">
                  <span className="text-sm opacity-60">💤</span>
                </div>
                <span className="text-sm text-moonlight">{t.sleepHours} (11pm-7am)</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 time-slot-current rounded"></div>
                <span className="text-sm text-moonlight">{t.currentTime}</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 time-slot-off rounded"></div>
                <span className="text-sm text-moonlight">Outside work hours</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 time-slot-selected rounded"></div>
                <span className="text-sm text-moonlight">Selected time</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-amber/20 border border-amber rounded"></div>
                <span className="text-sm text-moonlight">Different day</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="mt-8 text-center">
          <div className="divider-ornate mb-4">
            <span className="text-xs">✧</span>
          </div>
          <p className="text-moonlight/40 text-xs">
            MITime — Effective Time Coordination
          </p>
        </footer>
      </div>
    </div>
  );
};

export default TimezoneCompareApp;
